import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, 
  Loader2, 
  AlertTriangle, 
  Search, 
  Trash2, 
  Mail,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SurveyDuplicatesPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [duplicates, setDuplicates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  useEffect(() => {
    fetchDuplicates();
  }, []);

  const fetchDuplicates = async () => {
    try {
      const res = await axios.get(`${API}/admin/surveys/duplicates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDuplicates(res.data);
    } catch (err) {
      console.error('Failed to fetch duplicates:', err);
    } finally {
      setLoading(false);
    }
  };

  const searchCustomer = async () => {
    if (!searchEmail) return;
    setSearching(true);
    setSearchResult(null);
    try {
      const res = await axios.get(`${API}/admin/surveys/spam-check?email=${encodeURIComponent(searchEmail)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResult(res.data);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearching(false);
    }
  };

  const runCleanup = async (dryRun = true) => {
    setCleaning(true);
    setCleanupResult(null);
    try {
      const res = await axios.delete(`${API}/admin/surveys/cleanup-duplicates?dry_run=${dryRun}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCleanupResult(res.data);
      if (!dryRun) {
        fetchDuplicates(); // Refresh list
      }
    } catch (err) {
      console.error('Cleanup failed:', err);
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/survey-log')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-outfit">Survey Spam-kontrol</h1>
            <p className="text-slate-500 text-sm">Håndter duplikerede surveys og spam-beskyttelse</p>
          </div>
        </div>

        {/* Search */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Search className="h-5 w-5" />
              Tjek kunde
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                placeholder="Indtast kunde email..."
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchCustomer()}
                className="flex-1"
              />
              <Button onClick={searchCustomer} disabled={searching || !searchEmail}>
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Søg'}
              </Button>
            </div>

            {searchResult && (
              <div className="mt-4 p-4 bg-slate-100 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  {searchResult.would_be_blocked ? (
                    <>
                      <XCircle className="h-5 w-5 text-red-500" />
                      <span className="font-medium text-red-700">Blokeret af spam-beskyttelse</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span className="font-medium text-green-700">Kan modtage survey</span>
                    </>
                  )}
                </div>
                <p className="text-sm text-slate-600 mb-2">
                  <strong>Email:</strong> {searchResult.email}
                </p>
                <p className="text-sm text-slate-600 mb-2">
                  <strong>Antal surveys:</strong> {searchResult.total_surveys}
                </p>
                <p className="text-sm text-slate-600 mb-2">
                  <strong>Sendt sidste 30 dage:</strong> {searchResult.surveys_sent_last_30_days}
                </p>
                {searchResult.reason && (
                  <p className="text-sm text-red-600">
                    <strong>Årsag:</strong> {searchResult.reason}
                  </p>
                )}

                {searchResult.surveys?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-slate-700 mb-2">Survey historik:</p>
                    <div className="space-y-1">
                      {searchResult.surveys.slice(0, 5).map((s, i) => (
                        <div key={i} className="text-xs bg-white p-2 rounded flex justify-between">
                          <span>Booking: {s.hubhus_booking_id}</span>
                          <span className="text-slate-500">
                            {s.survey_email_sent_at ? `Sendt: ${s.survey_email_sent_at.slice(0, 10)}` : 'Ikke sendt'}
                          </span>
                          {s.submitted_at && <Badge variant="outline" className="text-green-600">Besvaret</Badge>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Duplicates */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Kunder med flere surveys
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => runCleanup(true)} disabled={cleaning}>
                  {cleaning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Preview oprydning
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => {
                    if (window.confirm('Er du sikker på at du vil slette duplikerede surveys? Dette kan ikke fortrydes.')) {
                      runCleanup(false);
                    }
                  }} 
                  disabled={cleaning}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Slet dubletter
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {cleanupResult && (
              <div className={`mb-4 p-4 rounded-lg ${cleanupResult.dry_run ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
                <p className="font-medium mb-2">
                  {cleanupResult.dry_run ? 'Preview af oprydning:' : 'Oprydning gennemført:'}
                </p>
                <ul className="text-sm space-y-1">
                  <li>Dubletter fundet: {cleanupResult.duplicates_found}</li>
                  <li>Surveys der {cleanupResult.dry_run ? 'vil blive' : 'blev'} slettet: {cleanupResult.surveys_to_delete}</li>
                  <li>Surveys der beholdes: {cleanupResult.surveys_kept}</li>
                  {cleanupResult.deleted_count !== undefined && (
                    <li className="text-green-700 font-medium">Faktisk slettet: {cleanupResult.deleted_count}</li>
                  )}
                </ul>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : duplicates?.customers?.length > 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-600 mb-3">
                  {duplicates.total_customers_with_duplicates} kunder har modtaget flere survey emails
                </p>
                {duplicates.customers.slice(0, 20).map((customer, idx) => (
                  <div key={idx} className="p-3 bg-slate-100 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="font-medium">{customer.customer_name}</span>
                        <span className="text-slate-500 text-sm ml-2">{customer.customer_email}</span>
                      </div>
                      <Badge variant="destructive">
                        <Mail className="h-3 w-3 mr-1" />
                        {customer.survey_count} surveys
                      </Badge>
                    </div>
                    <div className="text-xs text-slate-500 space-y-1">
                      {customer.surveys.slice(0, 3).map((s, i) => (
                        <div key={i}>
                          Booking {s.hubhus_booking_id} - Sendt: {s.survey_email_sent_at?.slice(0, 10) || 'N/A'}
                          {s.submitted_at && <span className="text-green-600 ml-2">(Besvaret)</span>}
                        </div>
                      ))}
                      {customer.surveys.length > 3 && (
                        <div className="text-slate-400">+{customer.surveys.length - 3} mere...</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500" />
                <p>Ingen dubletter fundet!</p>
                <p className="text-sm">Alle kunder har kun modtaget én survey.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SurveyDuplicatesPage;
